import type { DrinkEntry, SessionStats } from '@/types'
import { ETHANOL_DENSITY } from '@/utils/bac'
import moment from 'moment'

export type RiskLevel = 'LOW' | 'INCREASED' | 'HIGH'

export interface RiskAssessment {
  level: RiskLevel
  weeklyAvgStdDrinks: number
  maxSessionStdDrinks: number
  totalStdDrinks: number
}

export const STANDARD_DRINK_GRAMS = 10 // Australian standard drink = 10g pure alcohol

export function standardDrinks(entry: DrinkEntry): number {
  const ml = Number(entry.drink.milliliters)
  const abv = Number(entry.drink.alcohol)
  return (ml * (abv / 100) * ETHANOL_DENSITY) / STANDARD_DRINK_GRAMS
}

export function sessionStandardDrinks(session: SessionStats): number {
  return session.drinks.reduce((sum, e) => sum + standardDrinks(e), 0)
}

export function computeRiskAssessment(
  entries: DrinkEntry[],
  allSessions: SessionStats[]
): RiskAssessment | null {
  const nonGapEntries = entries.filter(e => !e.is_gap)
  if (nonGapEntries.length === 0 || allSessions.length < 2) return null

  const firstDate = moment(nonGapEntries[0].finished_at)
  const lastDate = moment(nonGapEntries[nonGapEntries.length - 1].finished_at)
  const totalDays = Math.max(1, lastDate.diff(firstDate, 'days') + 1)

  if (totalDays < 7) return null

  const totalStdDrinks = nonGapEntries.reduce((sum, e) => sum + standardDrinks(e), 0)
  const totalWeeks = Math.max(1, totalDays / 7)
  const weeklyAvgStdDrinks = totalStdDrinks / totalWeeks
  const maxSessionStdDrinks = Math.max(...allSessions.map(s => sessionStandardDrinks(s)))

  let level: RiskLevel
  if (weeklyAvgStdDrinks > 20 || maxSessionStdDrinks > 8) {
    level = 'HIGH'
  } else if (weeklyAvgStdDrinks > 10 || maxSessionStdDrinks > 4) {
    level = 'INCREASED'
  } else {
    level = 'LOW'
  }

  return { level, weeklyAvgStdDrinks, maxSessionStdDrinks, totalStdDrinks }
}
